import React, { useContext, useState } from "react";
import CustomerProfileContext from "../../common/components/context/StandAloneContext";
import { Pay } from "../FileDetail/Pay/Pay";
import { ROE } from "../FileDetail/ROE/ROE";
import { YearEndFile } from "../FileDetail/YearEnd/YearEnd";
import { FileType, Status } from "../../models/StandAloneData";
import { NavbarItem } from "../../common/components/navbar/navbar";
import { GetRoeImportById, GetPayrunImportById, UploadCsv, GetValidationErrorsByImportId, GetYearEndImportById, GetPaymentImportDetailById, GetTaxesImportDetailById, GetGarnishmentImportDetailById } from "../../services/ImportDetailService";
import { Modal, ModalBody, ModalFooter, ModalFooterActions, ModalHeader } from "@ceridianhcm/components";
import Banner from "../../common/components/banner/banner";
import logger from '../../logger';




interface IReducerProperties {
    formValues: any | null;
    page: number;
    currentMenu: NavbarItem[];
    progressMessage: string;
    loading: boolean;
}

interface IReducerAction {
    type: string;
    value?: any | null;
}
export function dataReducer(state: IReducerProperties, action: IReducerAction): IReducerProperties {
    let result: IReducerProperties = {
        ...state
    };

    switch (action.type) {
        case 'exportpayrollsummary':
            logger.info('Export button click event called..');
            break;
        case 'default':
            break;
        case 'toggleFilter':
            break;
    }
    return result;
}

interface FileModalProps {
    showModal: boolean;
    onCancel: () => void;

}


const generateROECsvRows = (roeData: any, fileName: string): string[] => {
    const headers = [
        "File Name",
        "Pay Date",
        "Period End Date",
        "ROE Count",
        "Total Insurable Hours",
        "Total Insurable Amount"
    ];
    const row = [
        fileName,
        new Date(roeData.payDate).toLocaleDateString(),
        new Date(roeData.periodEndDate).toLocaleDateString(),
        roeData.roeCount.toString(),
        roeData.totalInsurableHours.toString(),
        roeData.totalInsurableAmount.toString()
    ];
    return [headers.join(","), row.join(",")];
};

const generatePayCsvRows = (payData: any, fileName: string, paymentData: any[], remittanceData: any[], garnishmentRaw: any[]): string[] => {
    const headers = [
        "File Name",
        "Pay date",
        "Period End Date",
        "Payments",
        "Remittance",
        "Garnishment",
        "Total"
    ];
    const row = [
        fileName,
        new Date(payData.payDate).toLocaleDateString(),
        new Date(payData.periodEndDate).toLocaleDateString(),
        payData.payments.toString(),
        payData.remuneration.toString(),
        payData.garnishment.toString(),
        payData.total.toString()
    ];
    const csvRows = [headers.join(","), row.join(",")];

    const garnishmentData = garnishmentRaw.map((g: any) => ({
        Id: g.id,
        ImportDetails_Id: g.importDetails_Id,
        PaymentType: g.paymentType,
        CaseNumber: g.caseNumber,
        Name: g.name,
        PayeeId: g.payeeId,
        SIN: g.sin,
        VendorId: g.vendorId,
        Amount: g.amount
    }));

    addPaymentRows(csvRows, paymentData);
    addRemittanceRows(csvRows, remittanceData);
    addGarnishmentRows(csvRows, garnishmentData);

    return csvRows;
};

const addPaymentRows = (csvRows: string[], paymentData: any[]): void => {
    if (paymentData?.length) {
        csvRows.push("");
        csvRows.push("Payments");
        csvRows.push("Payment Type,Count,Amount");
        paymentData.forEach(p =>
            csvRows.push(`"${p.paymentType}","${p.count}","${p.amount}"`)
        );
    }
};

const addRemittanceRows = (csvRows: string[], remittanceData: any[]): void => {
    if (remittanceData?.length) {
        csvRows.push("");
        csvRows.push("Remittance");
        csvRows.push("Tax Type,Tax Number,Amount");
        remittanceData.forEach(r =>
            csvRows.push(`"${r.taxType}","${r.taxNumber}","${r.amount}"`)
        );
        const totalRemittance = remittanceData.reduce((sum, x) => sum + Number(x.amount), 0);
        csvRows.push(`"Total Remittance Amount","${totalRemittance.toFixed(2)}"`);
    }
};

const addGarnishmentRows = (csvRows: string[], garnishmentData: any[]): void => {
    if (garnishmentData?.length) {
        csvRows.push("");
        csvRows.push("Garnishment");
        csvRows.push("Payment Type,Case Number,Name,PayeeId,Amount");
        garnishmentData.forEach(r =>
            csvRows.push(`"${r.PaymentType}","${r.CaseNumber}","${r.Name}","${r.PayeeId}","${r.Amount}"`)
        );
    }
};

const generateYearEndCsvRows = (payData: any, fileName: string): string[] => {
    const headers = [
        "File Name",
        "Run Date",
        "Tax Year",
        "Company Name",
        "Slip Count"
    ];
    const row = [
        fileName,
        new Date(payData.runDate).toLocaleDateString(),
        payData.taxYear.toString(),
        payData.transmitterCompanyName,
        payData.t4SlipCount.toString(),
    ];
    return [headers.join(","), row.join(",")];
};

const generateErrorCsvRows = (errorData: any[]): string[] => {
    const headers = ["Error", "Message"];
    const errorRows = errorData.map(err => [
        "Error",
        err.message.replace(/"/g, '""')
    ]);
    const csvRows = [headers.join(",")];
    for (const row of errorRows) {
        csvRows.push(row.map(cell => `"${cell}"`).join(","));
    }
    return csvRows;
};

const downloadCsvFile = (blobUrl: string, fileName: string): void => {
    if (blobUrl && /^(blob:|https?:)\/\//.test(blobUrl)) {
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};

export const FileModal: React.FC<FileModalProps> = ({ showModal, onCancel }) => {
    const { state } = useContext(CustomerProfileContext);
    const [errors, setErrors] = useState(false);

    const exportROEData = async (importId: number, fileName: string): Promise<string[] | null> => {
        const roeData = await GetRoeImportById(importId);
        if (!roeData) {
            logger.warn("No ROE data");
            return null;
        }
        return generateROECsvRows(roeData, fileName);
    };

    const exportPayData = async (importId: number, fileName: string): Promise<string[] | null> => {
        const payData = await GetPayrunImportById(importId);
        if (!payData) {
            logger.warn("No Pay data found");
            return null;
        }
        const paymentData = await GetPaymentImportDetailById(payData.id);
        const remittanceData = await GetTaxesImportDetailById(payData.id, 0, 25);
        const garnishmentRaw = await GetGarnishmentImportDetailById(payData.id, 0, 25);
        return generatePayCsvRows(payData, fileName, paymentData, remittanceData, garnishmentRaw);
    };

    const exportYearEndData = async (importId: number, fileName: string): Promise<string[] | null> => {
        const payData = await GetYearEndImportById(importId);
        if (!payData) {
            logger.warn("No Year End data found");
            return null;
        }
        return generateYearEndCsvRows(payData, fileName);
    };

    const exportErrorData = async (importId: number): Promise<string[] | null> => {
        const errorData = await GetValidationErrorsByImportId(importId);
        if (!errorData || !Array.isArray(errorData)) {
            logger.warn("No error data found");
            return null;
        }
        return generateErrorCsvRows(errorData);
    };

    const getExportConfig = (fileType: FileType, formattedDate: string) => {
        const isYearEnd = fileType === FileType.T4_Original || fileType === FileType.T4A_Original || fileType === FileType.T4_Amended || fileType === FileType.T4A_Amended;
        return {
            screenName: isYearEnd ? "YearEnd Processing" : "Payroll Processing",
            fileName: isYearEnd ? `YearEnd_Processing_${formattedDate}.csv` : `PayrollProcessing_${formattedDate}.csv`
        };
    };

    const getCsvRows = async (importId: number, fileType: FileType, status: Status): Promise<string[] | null> => {
        if (status === Status.Error) {
            return await exportErrorData(importId);
        }

        switch (fileType) {
            case FileType.ROE:
                return await exportROEData(importId, state.fileName);
            case FileType.Pay:
                return await exportPayData(importId, state.fileName);
            case FileType.T4_Original:
            case FileType.T4A_Original:
            case FileType.T4_Amended:
            case FileType.T4A_Amended:
                return await exportYearEndData(importId, state.fileName);
            default:
                return null;
        }
    };

    const exportChildGrid = async (importId: number, fileType: FileType, status: Status) => {
        try {
            const formattedDate = new Date().toISOString().replace(/[:.-]/g, '').slice(0, 15);
            const config = getExportConfig(fileType, formattedDate);
            const csvRows = await getCsvRows(importId, fileType, status);

            if (!csvRows) {
                return;
            }

            const csvContent = csvRows.join("\n");
            const response = await UploadCsv(config.fileName, csvContent, config.screenName);
            downloadCsvFile(response.data, config.fileName);
        } catch (error) {
            setErrors(true);
            logger.error("Error fetching or exporting data:", error);
        }
    };

    return (
            <Modal
                id={"File Modal"}
                testId="Test"
                open={showModal}
                ariaLabelledBy={""}
                onClose={onCancel}
                size="fullscreen"
            >
                <ModalHeader
                    title={"File Details"}
                    id={"File Modal Header"}
                    onCloseButtonClick={onCancel}
                    closeButtonAriaLabel={"Close Modal"}
                    
                />
                <ModalBody id={"File Modal Body"}>
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px" }}>
                        <button
                            onClick={() => exportChildGrid(state.importId, state.fileType, state.status)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                background: "none",
                                color: "#0D60DF",
                                fontSize: "15px",
                                border: "none",
                                fontWeight: "600",
                                cursor: "pointer"
                            }}
                        >
                            <svg
                                data-evr-name="export"
                                viewBox="0 0 24 24"
                                className="icon-module__evrIcon__bo1Wg__1-37-20 icon-module__md__FUyGU__1-37-20"
                                fill="currentColor"
                                width={24}
                                height={24}
                                style={{ display: 'inline-block', width: '20px', height: '20px', flexShrink: 0 }}
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                            >
                                <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M15.2929 8.70711C15.6834 9.09763 16.3166 9.09763 16.7071 8.70711C17.0976 8.31658 17.0976 7.68342 16.7071 7.29289L12.7071 3.29289C12.3166 2.90237 11.6834 2.90237 11.2929 3.29289C11.2897 3.29603 11.2866 3.29918 11.2835 3.30235L7.29289 7.29299C6.90237 7.68351 6.90237 8.31668 7.29289 8.7072C7.68342 9.09772 8.31658 9.09772 8.70711 8.7072L11 6.41431L11 14C11 14.5523 11.4477 15 12 15C12.5523 15 13 14.5523 13 14L13 6.41424L15.2929 8.70711ZM2 13C2 12.4477 2.44772 12 3 12C3.55228 12 4 12.4477 4 13L4 19H20V13C20 12.4477 20.4477 12 21 12C21.5523 12 22 12.4477 22 13V19C22 20.1046 21.1046 21 20 21H4C2.89543 21 2 20.1046 2 19V13Z"
                                />
                            </svg>
                            Export
                        </button>
                    </div>
                    {errors && <Banner
                        errorStatus="Error: "
                        errorDescription="An internal server error occurred, check with administrator."
                    ></Banner>}

                    {state.fileType === FileType.Pay && <Pay />}
                    {state.fileType === FileType.ROE && <ROE />}
                {state.fileType === FileType.T4_Original && <YearEndFile />}
                {state.fileType === FileType.T4A_Original && <YearEndFile />}
                {state.fileType === FileType.T4_Amended && <YearEndFile />}
                {state.fileType === FileType.T4A_Amended && <YearEndFile />}
                </ModalBody>
                <ModalFooter id={`footer`}>
                    <ModalFooterActions
                        id={`action-buttons`}
                        primaryAction={{
                            id: `close-button`,
                            label: `Close`,
                            onClick: onCancel
                        }}
                    />
                </ModalFooter>
            </Modal>
    );
};